// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  pages/Estoque/RelatoriosEstoque.tsx
//  Tabs: Extrato Analítico  |  Giro de Estoque
// ════════════════════════════════════════════════════════════════════════════

import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { ChevronLeft, ChevronRight, BarChart2, TrendingUp } from "lucide-react";

const PAGE_SIZES = [10, 20, 50] as const;
import { useEstoqueAnalitico, useEstoqueGiro } from "@/features/estoque/hooks/useRelatoriosEstoque";

// ── helpers ──────────────────────────────────────────────────────────────────

const fmtQtd = (v: number) =>
  new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 4 }).format(v);

const fmtDate = (s: string) =>
  new Date(s).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });

const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

const TIPOS = [
  { id: "", label: "Todos os tipos" },
  { id: "1", label: "Entrada" },
  { id: "2", label: "Saída" },
  { id: "3", label: "Transferência" },
  { id: "4", label: "Ajuste Positivo" },
  { id: "5", label: "Ajuste Negativo" },
];

function tipoVariant(id: number): "default" | "destructive" | "secondary" | "outline" {
  if (id === 1) return "default";
  if (id === 2) return "destructive";
  if (id === 3) return "secondary";
  return "outline";
}

// ══════════════════════════════════════════════════════════════════════════════
//  Tab: Extrato Analítico
// ══════════════════════════════════════════════════════════════════════════════

function TabAnalitico() {
  const [pagina,        setPagina]        = useState(1);
  const [tamanhoPagina, setTamanhoPagina] = useState(20);
  const [dtInicio,      setDtInicio]      = useState(daysAgo(30));
  const [dtFim,         setDtFim]         = useState(today());
  const [tipoId,        setTipoId]        = useState<string>("");

  const { data, isLoading, isFetching } = useEstoqueAnalitico({
    dtInicio,
    dtFim,
    tipoId: tipoId ? Number(tipoId) : undefined,
    pagina,
    tamanhoPagina,
  });

  const total        = data?.total        ?? 0;
  const totalPaginas = data?.totalPaginas ?? 1;
  const firstItem    = total === 0 ? 0 : (pagina - 1) * tamanhoPagina + 1;
  const lastItem     = Math.min(pagina * tamanhoPagina, total);

  const handlePageSizeChange = (value: string) => { setTamanhoPagina(Number(value)); setPagina(1); };

  return (
    <div className="space-y-4">
      {/* Filtros */}
      <div className="flex flex-wrap gap-3 items-end">
        <div className="flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">De</span>
          <Input
            type="date"
            value={dtInicio}
            onChange={(e) => { setDtInicio(e.target.value); setPagina(1); }}
            className="w-40"
          />
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">Até</span>
          <Input
            type="date"
            value={dtFim}
            onChange={(e) => { setDtFim(e.target.value); setPagina(1); }}
            className="w-40"
          />
        </div>
        <Select value={tipoId} onValueChange={(v) => { setTipoId(v === "todos" ? "" : v); setPagina(1); }}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Tipo de movimento" />
          </SelectTrigger>
          <SelectContent>
            {TIPOS.map((t) => (
              <SelectItem key={t.id || "todos"} value={t.id || "todos"}>
                {t.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Tabela */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Data</TableHead>
              <TableHead>Produto</TableHead>
              <TableHead>Local</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead className="text-right">Qtd Mov.</TableHead>
              <TableHead className="text-right">Saldo Ant.</TableHead>
              <TableHead className="text-right">Saldo Atu.</TableHead>
              <TableHead>Justificativa</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading
              ? Array.from({ length: 8 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 8 }).map((_, j) => (
                      <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                    ))}
                  </TableRow>
                ))
              : !data?.itens.length
              ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                      Nenhum movimento encontrado no período.
                    </TableCell>
                  </TableRow>
                )
              : data.itens.map((item) => (
                  <TableRow key={item.movimentoId}>
                    <TableCell className="text-xs whitespace-nowrap">{fmtDate(item.dtAtualizacao)}</TableCell>
                    <TableCell className="font-medium text-sm">{item.produtoDescricao}</TableCell>
                    <TableCell className="text-sm">{item.localDescricao}</TableCell>
                    <TableCell>
                      <Badge variant={tipoVariant(item.tipoMovimentoId)}>
                        {item.tipoMovimentoDesc}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{fmtQtd(item.qtdMovimento)}</TableCell>
                    <TableCell className="text-right tabular-nums text-muted-foreground">{fmtQtd(item.qtdSaldoAnterior)}</TableCell>
                    <TableCell className="text-right tabular-nums font-medium">{fmtQtd(item.qtdSaldoAtual)}</TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">
                      {item.justificativa ?? "—"}
                    </TableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </div>

      {/* Paginação */}
      <div className="flex flex-wrap items-center justify-between gap-4 pt-2 border-t">
        <p className="text-sm text-muted-foreground">
          {total === 0 ? 'Nenhum registro'
            : `Mostrando ${firstItem}–${lastItem} de ${total} registro${total !== 1 ? 's' : ''}`}
          {isFetching && !isLoading && <span className="ml-2 text-xs">(atualizando...)</span>}
        </p>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground whitespace-nowrap">Itens por página:</span>
            <Select value={String(tamanhoPagina)} onValueChange={handlePageSizeChange}>
              <SelectTrigger className="w-[80px] h-8"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PAGE_SIZES.map((s) => <SelectItem key={s} value={String(s)}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina(1)} disabled={pagina === 1}>«</Button>
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina((p) => p - 1)} disabled={pagina === 1}><ChevronLeft className="h-4 w-4" /></Button>
            {Array.from({ length: Math.min(5, totalPaginas) }, (_, i) => {
              let page: number;
              if      (totalPaginas <= 5)         page = i + 1;
              else if (pagina <= 3)                page = i + 1;
              else if (pagina >= totalPaginas - 2) page = totalPaginas - 4 + i;
              else                                 page = pagina - 2 + i;
              return (
                <Button key={page} variant={pagina === page ? 'default' : 'outline'}
                  size="icon" className="h-8 w-8 text-xs" onClick={() => setPagina(page)}>{page}</Button>
              );
            })}
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina((p) => p + 1)} disabled={pagina >= totalPaginas}><ChevronRight className="h-4 w-4" /></Button>
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPagina(totalPaginas)} disabled={pagina >= totalPaginas}>»</Button>
          </div>
          <span className="text-sm text-muted-foreground whitespace-nowrap">Página {pagina} de {totalPaginas}</span>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  Tab: Giro de Estoque
// ══════════════════════════════════════════════════════════════════════════════

function TabGiro() {
  const [dtInicio, setDtInicio] = useState(daysAgo(30));
  const [dtFim, setDtFim] = useState(today());

  const { data, isLoading } = useEstoqueGiro({ dtInicio, dtFim });

  const giroMax = data?.reduce((m, i) => Math.max(m, i.giroEstoque), 0) ?? 0;

  return (
    <div className="space-y-4">
      {/* Filtros */}
      <div className="flex flex-wrap gap-3 items-end">
        <div className="flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">De</span>
          <Input type="date" value={dtInicio} onChange={(e) => setDtInicio(e.target.value)} className="w-40" />
        </div>
        <div className="flex flex-col gap-1">
          <span className="text-xs text-muted-foreground">Até</span>
          <Input type="date" value={dtFim} onChange={(e) => setDtFim(e.target.value)} className="w-40" />
        </div>
      </div>

      {/* Tabela */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Produto</TableHead>
              <TableHead>Local</TableHead>
              <TableHead className="text-right">Entradas</TableHead>
              <TableHead className="text-right">Saídas</TableHead>
              <TableHead className="text-right">Saldo Atual</TableHead>
              <TableHead className="text-right">Giro</TableHead>
              <TableHead>Intensidade</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading
              ? Array.from({ length: 6 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 7 }).map((_, j) => (
                      <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                    ))}
                  </TableRow>
                ))
              : !data?.length
              ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                      Nenhum dado de giro encontrado no período.
                    </TableCell>
                  </TableRow>
                )
              : data.map((item, idx) => {
                  const pct = giroMax > 0 ? (item.giroEstoque / giroMax) * 100 : 0;
                  return (
                    <TableRow key={idx}>
                      <TableCell className="font-medium">{item.produtoDescricao}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{item.localDescricao}</TableCell>
                      <TableCell className="text-right tabular-nums text-success">{fmtQtd(item.totalEntradas)}</TableCell>
                      <TableCell className="text-right tabular-nums text-destructive">{fmtQtd(item.totalSaidas)}</TableCell>
                      <TableCell className="text-right tabular-nums">{fmtQtd(item.saldoAtual)}</TableCell>
                      <TableCell className="text-right tabular-nums font-bold">
                        {item.giroEstoque.toFixed(2)}×
                      </TableCell>
                      <TableCell className="w-40">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-2 rounded bg-muted overflow-hidden">
                            <div
                              className="h-full rounded bg-primary transition-all"
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
          </TableBody>
        </Table>
      </div>

      {data && data.length > 0 && (
        <p className="text-xs text-muted-foreground">
          {data.length} produto(s) com movimentação — ordenado por maior giro.
        </p>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  Página Principal
// ══════════════════════════════════════════════════════════════════════════════

export default function RelatoriosEstoque() {
  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Relatórios de Estoque</h1>
        <p className="text-muted-foreground mt-1">
          Extrato analítico de movimentos e análise de giro de estoque
        </p>
      </div>

      <Card className="shadow-soft">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart2 className="h-5 w-5 text-primary" />
            Análises de Estoque
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="analitico">
            <TabsList className="mb-4">
              <TabsTrigger value="analitico" className="gap-2">
                <BarChart2 className="h-4 w-4" />
                Extrato Analítico
              </TabsTrigger>
              <TabsTrigger value="giro" className="gap-2">
                <TrendingUp className="h-4 w-4" />
                Giro de Estoque
              </TabsTrigger>
            </TabsList>

            <TabsContent value="analitico">
              <TabAnalitico />
            </TabsContent>

            <TabsContent value="giro">
              <TabGiro />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
